import { createContext, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface TransitionContextType {
  navigateWithTransition: (path: string) => void;
  overlayRef: React.RefObject<HTMLDivElement>;
}

const TransitionContext = createContext<TransitionContextType | null>(null);

export const useTransition = () => {
  const context = useContext(TransitionContext);
  if (!context) {
    throw new Error('useTransition must be used within TransitionProvider');
  }
  return context;
};

export const TransitionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const overlayRef = useRef<HTMLDivElement>(null);

  const navigateWithTransition = useCallback((path: string) => {
    const overlay = overlayRef.current;
    if (!overlay) {
      navigate(path);
      return;
    }

    // Kill all ScrollTriggers PRIMA della transizione
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    gsap.killTweensOf('*');

    // Animazione overlay in entrata (dal basso)
    gsap.set(overlay, { yPercent: 100, display: 'block' });
    gsap.to(overlay, {
      yPercent: 0,
      duration: 0.3,
      ease: 'power2.out',
      onComplete: () => {
        // Scroll to top mentre overlay copre
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        // Naviga
        navigate(path);

        // Scroll di nuovo dopo navigate
        setTimeout(() => {
          window.scrollTo(0, 0);

          // Fai uscire l'overlay
          gsap.to(overlay, {
            yPercent: -100,
            duration: 0.4,
            ease: 'power2.inOut',
            onComplete: () => {
              gsap.set(overlay, { display: 'none' });
            }
          });
        }, 100);
      }
    });
  }, [navigate]);

  return (
    <TransitionContext.Provider value={{ navigateWithTransition, overlayRef }}>
      {children}
      <div
        ref={overlayRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#2E5339',
          zIndex: 9999,
          display: 'none',
          pointerEvents: 'none'
        }}
      />
    </TransitionContext.Provider>
  );
};
